// src/ui/components/EventModal.suppliers.js
// Tarjeta "Proveedores" de la ficha del evento: qué proveedor hace cada
// servicio (tarima, luces, sonido, transporte), si está confirmado y una nota
// para él. Se ve desde que se envía el presupuesto (canAssignSuppliers) y solo
// para admin y manager. Cada cambio se guarda al momento en event_suppliers.
//
// Respeta el modo solo lectura de la ficha: setEditMode habilita o deshabilita
// todos los select/textarea del modal, también los de esta tarjeta.

import { actions } from "../../app/state.js";
import { listContactsByTeam } from "../../services/contactsService.js";
import { listEventSuppliers, saveEventSuppliers } from "../../services/eventSuppliersService.js";
import {
  EVENT_SUPPLIER_SERVICES, SUPPLIER_LINE_STATUS, canAssignSuppliers, supplierCandidates,
  supplierDisplayName, supplierServiceLabel, technicalSupplierRows,
} from "../../domain/suppliers.js";
import { $, escapeHtml } from "./EventModal.utils.js";

const STATUS_LABELS = Object.freeze({
  [SUPPLIER_LINE_STATUS.PROPOSED]: "Propuesto",
  [SUPPLIER_LINE_STATUS.CONFIRMED]: "Confirmado",
});

const FIELD_CLASS = "w-full text-sm rounded-lg border border-slate-200 bg-white px-2 py-1.5";

function renderRow(service, line, contacts, editable) {
  const off = editable ? "" : " disabled";
  const contactId = line?.contact_id || "";
  const options = supplierCandidates(contacts, service, contactId)
    .map(c => `<option value="${escapeHtml(c.id)}"${c.id === contactId ? " selected" : ""}>${escapeHtml(supplierDisplayName(c))}</option>`)
    .join("");
  const statusOptions = Object.entries(STATUS_LABELS)
    .map(([value, label]) => `<option value="${value}"${line?.status === value ? " selected" : ""}>${label}</option>`)
    .join("");
  const statusOff = editable && contactId ? "" : " disabled";

  return `
      <div class="space-y-1" data-supplier-service="${service}">
        <div class="text-xs font-bold text-slate-600">${escapeHtml(supplierServiceLabel(service))}</div>
        <div class="grid grid-cols-3 gap-2">
          <select class="${FIELD_CLASS} col-span-2" data-supplier-field="contact_id"${off}>
            <option value="">Sin proveedor</option>${options}
          </select>
          <select class="${FIELD_CLASS}" data-supplier-field="status"${statusOff}>${statusOptions}</select>
        </div>
        <textarea class="${FIELD_CLASS} text-xs" rows="1" data-supplier-field="notes_for_supplier"
          placeholder="Nota para el proveedor"${off}>${escapeHtml(line?.notes_for_supplier || "")}</textarea>
      </div>`;
}

export function renderSuppliersCardRows(lines, contacts, { editable }) {
  const byService = new Map((lines || []).map(l => [l.service, l]));
  return EVENT_SUPPLIER_SERVICES.map(service => renderRow(service, byService.get(service), contacts, editable)).join("");
}

// Área Técnica de producción: tarima, luces y sonido en solo lectura (antes
// eran tareas). Se cambian en la tarjeta Proveedores.
export function renderTechnicalSuppliersSummary(lines, contacts) {
  if (!Array.isArray(lines)) {
    return '<p class="text-xs text-rose-600">No se pudieron cargar los proveedores. Recarga la ficha.</p>';
  }
  const byId = new Map((contacts || []).map(c => [c.id, c]));
  const rows = technicalSupplierRows(lines, byId).map(r => `
      <div class="rounded-xl border ${r.confirmed ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"} p-3" data-technical-supplier="${r.service}">
        <div class="mb-1 flex items-center justify-between gap-2">
          <span class="text-[10px] font-black uppercase tracking-wider text-slate-500">${escapeHtml(r.label)}</span>
          <span class="text-[10px] font-bold ${r.confirmed ? "text-emerald-700" : "text-slate-400"}">${escapeHtml(STATUS_LABELS[r.status] || "Pendiente")}</span>
        </div>
        <div class="text-sm ${r.name ? "text-slate-800" : "text-slate-400"}">${escapeHtml(r.name || "Sin proveedor")}</div>
        ${r.notes ? `<p class="mt-1 text-xs text-slate-500 whitespace-pre-line">${escapeHtml(r.notes)}</p>` : ""}
      </div>`).join("");
  return `
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">${rows}</div>
    <p class="mt-2 text-[11px] text-slate-400">Se eligen en la tarjeta Proveedores (pestaña Datos Generales).</p>`;
}

export async function wireSuppliersCard(root, ev, { role, teamId } = {}) {
  const card = $(root, "#event-suppliers-card");
  const list = $(root, "#event-suppliers-list");
  if (!card || !list || !ev?.id) return;
  if (!(role === "admin" || role === "manager") || !canAssignSuppliers(ev.stage)) {
    card.classList.add("hidden");
    return;
  }
  card.classList.remove("hidden");

  let lines = [];
  let contacts = [];
  // Los guardados van de uno en uno: cada uno parte de las líneas recién
  // leídas, así dos cambios seguidos no chocan entre sí.
  let queue = Promise.resolve();

  try {
    [lines, contacts] = await Promise.all([
      listEventSuppliers(ev.id),
      listContactsByTeam(teamId, {
        orderBy: "name",
        ascending: true,
        select: "id, name, surname, company, is_supplier, supplier_type, supplier_types",
      }),
    ]);
  } catch (e) {
    list.innerHTML = `<p class="text-xs text-rose-600">${escapeHtml(e?.message || "No se pudieron cargar los proveedores.")}</p>`;
    return;
  }
  const isEditable = () => root.getAttribute("data-is-editable") === "true";
  list.innerHTML = renderSuppliersCardRows(lines, contacts, { editable: isEditable() });

  // Solo se vuelve a pintar la fila guardada, para no pisar lo que se esté
  // escribiendo en otra.
  function repaintRow(service) {
    const row = list.querySelector(`[data-supplier-service="${service}"]`);
    if (!row) return;
    row.outerHTML = renderRow(service, lines.find(l => l.service === service), contacts, isEditable());
  }

  async function save(service, field, value, contactId) {
    let choice;
    if (field === "contact_id") choice = value ? { contact_id: value } : null;
    else if (!contactId) {
      actions.addToast?.({ type: "info", message: `${supplierServiceLabel(service)}: elige antes el proveedor.` });
      repaintRow(service);
      return;
    } else choice = { contact_id: contactId, [field]: value };

    try {
      lines = await listEventSuppliers(ev.id);
      await saveEventSuppliers(ev.id, lines, { [service]: choice });
      actions.addToast?.({ type: "success", message: `${supplierServiceLabel(service)}: guardado.` });
    } catch (e) {
      actions.addToast?.({ type: "error", message: e?.message || "No se pudo guardar el proveedor." });
    }
    try {
      lines = await listEventSuppliers(ev.id);
    } catch { /* se queda la última lectura */ }
    repaintRow(service);
    // Para que el área Técnica de producción se actualice sin reabrir la ficha.
    root.dispatchEvent(new CustomEvent("event-suppliers:changed", { detail: { lines } }));
  }

  list.addEventListener("change", (e) => {
    const field = e.target?.dataset?.supplierField;
    const row = e.target?.closest("[data-supplier-service]");
    if (!field || !row) return;
    const service = row.dataset.supplierService;
    const value = e.target.value ?? "";
    const contactId = row.querySelector('[data-supplier-field="contact_id"]')?.value || "";
    queue = queue.then(() => save(service, field, value, contactId));
  });
}
